import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import {
  SetSkillSubscriptionRequest,
  SkillSubscriptionRecord,
} from "../SkillRecord";

import { skillsQueryKey } from "./useSkills";

interface Args {
  skillId: string;
  request: SetSkillSubscriptionRequest;
}

/**
 * Pin (or clear) the caller's AI-available version for a skill.
 *
 * Upsert semantics: creates the subscription row if missing, otherwise
 * updates `ai_version` in place. Used by the "Available to AI" toggle on the
 * Personal tab — toggle on writes `ai_version = <latest_version>`, toggle off
 * writes `ai_version = null`. Invalidates both tab listings.
 */
export function useSetSubscription(orgId: string | undefined) {
  const { skillService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<SkillSubscriptionRecord, Error, Args>({
    mutationFn: ({ skillId, request }) =>
      skillService.setSubscription(skillId, request, { orgId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: skillsQueryKey(orgId) });
    },
  });
}
